import React from "react";
import { Modal } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import CustomButton from "../../../pages/reusable_components/custom_button_component/Button";
import styles from "./AlertModal.module.css";

export type AlertType = "warning" | "error" | "info" | "success";

export interface AlertModalProps {
  show: boolean;
  onHide: () => void;
  title?: string;
  size?: "sm" | "lg" | "xl" | undefined;
  message?: string;
  listItems?: string[];
  confirmText?: string; // kept for compatibility
  type?: AlertType;
  bodyContent?: React.ReactNode;
  footerButtons?: { label: string; onClick: () => void; variant?: string; progress?: boolean, type?: string; disabled?: boolean; }[];
}

const getIconByType = (type: AlertType) => {
  switch (type) {
    case "error":
      return <Icon.XCircleFill className={`${styles.icon} ${styles.error}`} />;
    case "info":
      return <Icon.InfoCircleFill className={`${styles.icon} ${styles.info}`} />;
    case "success":
      return <Icon.CheckCircleFill className={`${styles.icon} ${styles.success}`} />;
    case "warning":
    default:
      return <Icon.ExclamationTriangleFill className={`${styles.icon} ${styles.warning}`} />;
  }
};

export const AlertModal: React.FC<AlertModalProps> = ({
  show,
  onHide,
  title = "Alert",
  size = "lg",
  message = "Please review the following information:",
  listItems = [],
  type = "warning",
  bodyContent,
  footerButtons
}) => {
  const icon = getIconByType(type);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={size}
      centered
      keyboard={false}
      dialogClassName={styles.dialog}
      contentClassName={styles.content}
      backdropClassName={styles.backdrop}
      aria-labelledby="alert-modal-title"
    >
      <Modal.Header closeButton className={styles.header}>
        <Modal.Title id="alert-modal-title" className={styles.title}>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        {message && (
          <div className={styles.messageRow}>
            {icon}
            <h6 className={styles.message}>{message}</h6>
          </div>
        )}

        {bodyContent && (
          <div style={{ marginTop: message ? "1rem" : 0 }}>
            {bodyContent}
          </div>
        )}

        {listItems.length > 0 && (
          <ul className={styles.list}>
            {listItems.map((item) => (
              <li key={item} className={styles.listItem}>
                {item}
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>

      {footerButtons && footerButtons.length > 0 && (
        <Modal.Footer className={styles.footer}>
          {footerButtons.map((btn, i) => (
            <CustomButton
              key={`${btn.label}-${i}`}
              content={btn.label}
              progress={btn.progress}
              type={btn.type}
              disabled={btn.disabled}
              onclick={btn.onClick}
            />
          ))}
        </Modal.Footer>
      )}
    </Modal>
  );
};
